import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useUpdateVehicle } from '../../lib/queries';
import { can, useSession } from '../../lib/session';
import { cleanVin, isVinShaped, VIN_LENGTH, type FoundVin } from '../../lib/vin';
import { Button, Field } from '../ui';

type Reading = 'idle' | 'reading' | 'found' | 'none' | 'failed';

/** A VIN photo just taken; `scanned` is what the live scanner already read, if it was used. */
export interface FreshVin {
  source: Blob;
  key: number;
  scanned?: FoundVin | null | undefined;
}

export interface VinVehicle {
  id: string;
  customerId: string;
  vin: string | null;
}

/**
 * The VIN read off the photo of the plate and put on the car's record.
 *
 * A fresh photo is read straight away. A barcode read goes on the record at
 * once if the car has no VIN yet; text read off a plate is put in the field
 * for a person to check and save. A VIN already on the record is never
 * overwritten behind anyone's back. Whatever was read can be corrected by hand.
 */
export function VinPanel({ vehicle, fresh, stored }: {
  vehicle: VinVehicle;
  /** The photo just taken, read as soon as it arrives; `key` changes with every new shot. */
  fresh: FreshVin | null;
  /** The VIN photo already on the inspection, for reading again. */
  stored: string | null;
}) {
  const { t } = useI18n();
  const update = useUpdateVehicle(vehicle.customerId);
  const { data: session } = useSession();
  // Reading the plate is part of the walk-round; writing it on the car's record is editing the car.
  const mayWrite = can(session, 'vehicles.update');
  const [reading, setReading] = useState<Reading>('idle');
  const [found, setFound] = useState<FoundVin | null>(null);
  const [value, setValue] = useState(vehicle.vin ?? '');
  const [savedRead, setSavedRead] = useState(false);
  const latestVin = useRef(vehicle.vin);
  latestVin.current = vehicle.vin;

  const save = (vin: string, fromPhoto: boolean) =>
    update.mutate({ id: vehicle.id, vin }, { onSuccess: () => setSavedRead(fromPhoto) });

  const apply = (result: FoundVin) => {
    setFound(result);
    setValue(result.vin);
    setReading('found');
    // Only a barcode goes on the record by itself; a plate read as text waits for a person to check it.
    if (mayWrite && !latestVin.current && result.fromBarcode) save(result.vin, true);
  };

  const read = async (source: Blob | string) => {
    setReading('reading');
    setSavedRead(false);
    try {
      const { readVin } = await import('../../lib/vin-reader');
      const result = await readVin(source);
      if (result) apply(result);
      else setReading('none');
    } catch {
      setReading('failed');
    }
  };

  useEffect(() => {
    if (!fresh) return;
    // The scanner has already read it; a photo taken any other way is read now.
    if (fresh.scanned) { setSavedRead(false); apply(fresh.scanned); }
    else void read(fresh.source);
    // Once per new photo; the key is what changes.
  }, [fresh?.key]);

  const current = vehicle.vin;
  const differs = reading === 'found' && found && current && current !== found.vin;
  const typed = value.length > 0 && value !== current;

  let status: { text: string; tone: string } | null = null;
  if (reading === 'reading') status = { text: t('vin.reading'), tone: 'text-ink-dim' };
  else if (reading === 'none') status = { text: t('vin.none'), tone: 'text-warning' };
  else if (reading === 'failed') status = { text: t('vin.failed'), tone: 'text-warning' };
  else if (reading === 'found' && found) {
    if (differs) status = { text: t('vin.differs', { vin: current! }), tone: 'text-warning' };
    else if (savedRead) status = { text: t('vin.saved'), tone: 'text-positive' };
    else if (current === found.vin) status = { text: t('vin.matches'), tone: 'text-positive' };
    else status = { text: t('vin.check'), tone: 'text-warning' };
  }

  return (
    <div className="mt-3 rounded-xl border border-line bg-raised/60 p-3">
      <Field
        label={t('vin.label')}
        value={value}
        maxLength={VIN_LENGTH}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        className="tabular font-mono tracking-wider"
        onChange={(e) => setValue(cleanVin(e.target.value).slice(0, VIN_LENGTH))}
      />

      <div className="mt-1.5 flex min-h-9 items-center justify-between gap-2">
        {reading === 'reading' ? (
          <p className="flex items-center gap-2 text-xs text-ink-dim">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            {status?.text}
          </p>
        ) : (
          <p className={`text-xs leading-snug ${status?.tone ?? ''}`}>{status?.text}</p>
        )}
        {stored && reading !== 'reading' && (
          <button
            type="button"
            onClick={() => void read(stored)}
            className="min-h-9 shrink-0 rounded-lg px-2 text-xs font-semibold text-brand transition hover:bg-brand-wash"
          >
            {t('vin.readAgain')}
          </button>
        )}
      </div>
      {value.length > 0 && !isVinShaped(value) && <p className="mt-1 text-xs text-ink-faint">{t('vin.shape')}</p>}

      {mayWrite && typed && isVinShaped(value) && (
        <Button className="mt-2 w-full" loading={update.isPending} onClick={() => save(value, false)}>
          {differs && value === found?.vin ? t('vin.replace') : t('vin.save')}
        </Button>
      )}
    </div>
  );
}
